<script setup>

import ChipTemplate from '@/components/shared/ChipTemplate.vue'

const props = defineProps({
  border: {
    type: Boolean,
    default: false,
  },
  borderColor: {
    type: String,
    default: 'grey-3',
  },
  avatar: {
    type: String,
    required: false,
  },
  avatarIcon: {
    type: String,
    required: false,
  },
  avatarIconColor: {
    type: String,
    required: false,
  },
  hideAvatar: {
    type: Boolean,
    default: false
  },
  hideRightCaption: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    required: false,
  },
  // Secondary text shown inline right after the label, separated by a dash and greyed out.
  labelSuffix: {
    type: String,
    required: false,
  },
  caption: {
    type: String,
    required: false,
  },
  chips: {
    type: Array,
    required: false,
  },
  color: {
    type: String,
    required: false,
  },
  captionChips: {
    type: Array,
    required: false,
  },
  icon: {
    type: String,
    required: false,
  },
  iconColor: {
    type: String,
    required: false,
  },
  secondaryIcon: {
    type: String,
    required: false,
  },
  secondaryIconColor: {
    type: String,
    required: false,
  },
  rightLabel: {
    type: String,
    required: false,
  },
  rightCaption: {
    type: String,
    required: false,
  },
  rightChips: {
    type: Array,
    required: false,
  },
  rightCaptionChips: {
    type: Array,
    required: false,
  },
  clickable: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  dense: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['click', 'iconClick', 'secondaryIconClick'])

</script>
<template>
  <q-item
    :v-ripple="props.clickable"
    :clickable="props.clickable"
    class="q-px-none"
    :class="{ 'q-px-none': props.hideAvatar, 'dx-disabled-row': props.disabled }"
    :style="{ 'min-width': '100%'}"
    @click="emit('click')"
  >
    <slot name="left">
      <q-item-section
        v-if="!hideAvatar"
        avatar
        class="align-middle align-center q-px-none"
      >
        <slot name="avatar">
          <q-avatar>
            <q-img
              v-if="props.avatar"
              :src="props.avatar"
            />
            <q-icon
              v-else
              :name="props.avatarIcon || 'mdi-camera-off'"
              size="sm"
              :color="props.avatarIcon ? (props.avatarIconColor ? props.avatarIconColor : (props.color ? props.color : 'grey-5')) : 'grey-5'"
            />
          </q-avatar>
        </slot>
      </q-item-section>
      <q-item-section
        :style="{ 'flex-direction': 'column', 'align-items': 'left', 'text-wrap': 'wrap', 'border-left': props.border ? '.25rem solid' : 'none', 'border-color': props.border ? props.borderColor : 'transparent', 'padding-left': props.border ? '.5rem' : undefined }"
      >
        <q-item-label
          v-if="props.label || props.labelSuffix || props.chips"
          class="text-left"
        >
          <span
            :style="{color: props.color}"
          >
            <span v-if="props.label"> {{ props.label }} </span>
          </span>
          <span v-if="props.labelSuffix" class="text-grey q-ml-xs">- {{ props.labelSuffix }}</span>
          <chip-template
            v-if="props.chips"
            :values="props.chips"
            :style="{ 'margin-left': props.label ? '0.25rem' : '0rem' }"
            :dense="props.dense"
          />
        </q-item-label>
        <slot name="label" />
        <q-item-label
          v-if="props.caption || props.captionChips || $slots['caption']"
          caption
          class="text-left text-wrap"
        >
          <slot name="caption" />
          <chip-template
            v-if="props.captionChips"
            :values="props.captionChips"
            :style="{ 'margin-left': '0rem' }"
            :dense="props.dense"
          />
          <span
            v-if="props.caption && !$slots['caption']"
            :style="{color: props.color}"
            :class="{ 'q-mr-xs': props.captionChips && props.captionChips.length > 0 }"
          >{{ `${ props.captionChips && props.captionChips.length > 0 ? ' - ' : '' }${props.caption}` }}</span>
        </q-item-label>
      </q-item-section>
    </slot>
    <slot name="right">
      <q-item-section
        v-if="props.secondaryIcon"
        avatar
        class="align-middle align-center q-px-none"
      >
        <q-icon
          :name="props.secondaryIcon"
          flat
          :color="props.secondaryIconColor || 'grey-5'"
          @click.stop="emit('secondaryIconClick')"
        />
      </q-item-section>
      <q-item-section
        v-if="props.rightLabel || props.rightChips || props.rightCaption || props.rightCaptionChips"
        avatar
        class="align-middle align-center q-px-none"
      >
        <q-item-label
          v-if="props.rightLabel || props.rightChips"
          class="text-left"
        >
          <chip-template
            v-if="props.rightChips"
            :values="props.rightChips"
            justify="flex-end"
            :dense="props.dense"
          />
          <span
            v-if="props.rightLabel"
            :style="{ color: props.color, 'margin-left': props.rightChips ? '.2rem' : undefined }"
          >{{ props.rightLabel }}</span>
        </q-item-label>
        <q-item-label
          v-if="(props.rightCaption || props.rightCaptionChips || $slots['right-caption']) && !props.hideRightCaption"
          caption
          class="text-left"
        >
          <slot name="right-caption" />
          <span
            v-if="props.rightCaption && !$slots['right-caption']"
            :style="{color: props.color}"
          >{{ props.rightCaption }}</span>
          <chip-template
            v-if="props.rightCaptionChips"
            :values="props.rightCaptionChips"
            :style="{ margin: 0 }"
            justify="flex-end"
            :dense="props.dense"
          />
        </q-item-label>
      </q-item-section>
      <q-item-section
        v-if="props.icon"
        avatar
        class="align-middle align-center q-px-none"
      >
        <q-icon
          :name="props.icon"
          flat
          :color="props.iconColor || 'grey-5'"
          @click.stop="emit('iconClick')"
        />
      </q-item-section>
      <slot name="actions" />
    </slot>
  </q-item>
</template>
